/**
 * Helpers for showing and quoting received email HTML.
 *
 * Inbound mail is stored after the backend's HTML filter, which drops Outlook's <style> block and the &nbsp; in
 * its blank-line paragraphs. Outlook relies on that stylesheet (p.MsoNormal has no margin, list paragraphs are
 * indented 36pt), so these helpers put the essentials back as inline styles, which both the viewer and TinyMCE keep.
 */

const ACTIVE_ELEMENTS = "script, iframe, object, embed, base, meta[http-equiv]";
const URL_ATTRIBUTES = /^(href|src|action|formaction|xlink:href)$/;

// Inline wrappers Outlook leaves in an empty paragraph; anything else (an image, a link) is content.
const SPACER_TAGS = ["BR", "SPAN", "O:P", "FONT", "B", "I", "U"];

// nbsp, zero-width space (Outlook) and the TinyMCE caret
const INVISIBLE_TEXT = /[ ​﻿\s]/g;

/** Removes scripts, frames, event handlers and javascript: URLs so stored mail cannot run code in the app. */
export function stripActiveContent(doc: Document) {
    doc.querySelectorAll(ACTIVE_ELEMENTS).forEach(el => el.remove());
    doc.querySelectorAll("*").forEach(el => {
        for (const attr of Array.from(el.attributes)) {
            const name = attr.name.toLowerCase();
            if (name.startsWith("on") || (URL_ATTRIBUTES.test(name) && /^\s*javascript:/i.test(attr.value))) {
                el.removeAttribute(attr.name);
            }
        }
    });
}

/**
 * Copies a document's <style> rules onto the elements they match as inline styles, without overriding styles
 * the element already sets inline. TinyMCE drops <style> elements in the body, so a quoted email keeps its look
 * only through inline styles. @media, @font-face and @page rules are skipped.
 */
export function inlineStyleSheets(doc: Document) {
    const original = new Map<HTMLElement, Set<string>>();
    const ownProperties = (el: HTMLElement) => {
        let props = original.get(el);
        if (!props) {
            props = new Set(Array.from(el.style));
            original.set(el, props);
        }
        return props;
    };

    for (const sheet of Array.from(doc.styleSheets)) {
        let rules: CSSRuleList;
        try {
            rules = sheet.cssRules;
        } catch {
            continue;
        }

        for (const cssRule of Array.from(rules)) {
            // a plain style rule; @media, @font-face and @page rules are skipped
            if (cssRule.type !== CSSRule.STYLE_RULE) continue;
            const rule = cssRule as CSSStyleRule;

            let matches: NodeListOf<HTMLElement>;
            try {
                matches = doc.querySelectorAll<HTMLElement>(rule.selectorText);
            } catch {
                continue; // a selector the browser cannot query, e.g. a vendor pseudo-element
            }

            matches.forEach(el => {
                const own = ownProperties(el);
                for (const prop of Array.from(rule.style)) {
                    if (!own.has(prop)) {
                        el.style.setProperty(prop, rule.style.getPropertyValue(prop));
                    }
                }
            });
        }
    }
}

function isSpacerParagraph(p: Element): boolean {
    const text = (p.textContent ?? "").replace(INVISIBLE_TEXT, "");
    const hasRealElement = Array.from(p.querySelectorAll("*")).some(el => !SPACER_TAGS.includes(el.tagName.toUpperCase()));
    return text === "" && !hasRealElement;
}

/**
 * Collapses each run of empty Outlook/Word spacer paragraphs (<p class="MsoNormal">) to a single blank line, and
 * removes TinyMCE editor leftovers. One blank line keeps the sender's paragraph spacing; the runs are what piled up
 * in quoted threads.
 */
export function collapseEmptyMsoParagraphs(root: ParentNode) {
    // editor-only nodes, never part of the real content
    root.querySelectorAll('[data-mce-bogus="all"], span[data-mce-type="bookmark"]').forEach(el => el.remove());
    root.querySelectorAll("br[data-mce-bogus]").forEach(br => {
        const line = br.parentElement;
        // the placeholder is what gives an empty line its height, so keep it as a plain <br>
        if (line && line.children.length === 1 && (line.textContent ?? "").replace(INVISIBLE_TEXT, "") === "") {
            br.removeAttribute("data-mce-bogus");
        } else {
            br.remove();
        }
    });

    root.querySelectorAll("p.MsoNormal").forEach(p => {
        if (!p.isConnected || !isSpacerParagraph(p)) return;

        // the inbound filter empties Outlook's <o:p>&nbsp;</o:p>, which leaves a paragraph with no height
        p.innerHTML = "&nbsp;";

        let next = p.nextElementSibling;
        while (next && next.matches("p.MsoNormal") && isSpacerParagraph(next)) {
            const following = next.nextElementSibling;
            next.remove();
            next = following;
        }
    });
}

/**
 * Restores the Outlook formatting the stored HTML lost with its stylesheet: unspaced MsoNormal paragraphs,
 * indented list paragraphs, Symbol-font bullets and blank lines.
 */
export function restoreOutlookFormatting(root: ParentNode) {
    root.querySelectorAll<HTMLElement>("p.MsoNormal, li.MsoNormal, div.MsoNormal").forEach(el => {
        if (!el.style.marginTop) el.style.marginTop = "0";
        if (!el.style.marginBottom) el.style.marginBottom = "0";
    });

    root.querySelectorAll<HTMLElement>("p[class^='MsoListParagraph']").forEach(el => {
        if (!el.style.marginTop) el.style.marginTop = "0";
        if (!el.style.marginBottom) el.style.marginBottom = "0";
        if (!el.style.marginLeft) el.style.marginLeft = "36pt";
    });

    // Outlook writes a bullet as "·" in the Symbol font, which browsers do not map to a bullet
    root.querySelectorAll<HTMLElement>("span").forEach(span => {
        if (!/symbol/i.test(span.style.fontFamily)) return;

        const first = span.firstChild;
        const text = first?.nodeType === Node.TEXT_NODE ? (first.textContent ?? "") : "";
        if (text.startsWith("·")) {
            first!.textContent = "•" + text.slice(1);
            span.style.removeProperty("font-family");
        }
    });

    collapseEmptyMsoParagraphs(root);
}

/**
 * Keeps email content within the width it is shown in, as Outlook on the web does, so no horizontal scroll bar
 * appears: oversized images, media, tables and fixed-width blocks shrink to fit, and long words, URLs and
 * preformatted text wrap. Only an image given a width has its height freed to scale with it; one sized by its
 * height alone keeps that height rather than growing to its natural size.
 */
export const FIT_TO_WIDTH_CSS =
    " body { overflow-wrap: anywhere; } body * { max-width: 100%; }" +
    " img, video, canvas, svg { max-width: 100% !important; }" +
    ' img[width], img[style*="width"], video[width], svg[width] { height: auto !important; }' +
    " td, th { overflow-wrap: anywhere; } pre { white-space: pre-wrap; } ";

/** A CSS-safe font-family name: drops the characters that could close the declaration or the <style>. */
export function cssFontName(font: string | undefined, fallback: string): string {
    const clean = (font ?? "").replace(/["'<>;{}\\]/g, "").trim();
    return clean === "" ? fallback : clean;
}
